import { useCallback, type AnimationEvent, type KeyboardEvent } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { fadeOut } from 'components/utils/animations'
import zIndex from 'styles/tools/z-index'
import type { TextDirection } from 'lib/dom/logicalScroll'
import { useVoicePromptPosition } from './hooks/useVoicePromptPosition'

const TAIL_SIZE = 7

const messages = defineMessages({
  title: {
    id: 'Awl1gV1O1BDrDyyKV960',
    defaultMessage: 'Try planning by voice',
    description: 'Title of the tip under the TripPlanner landing page microphone button inviting travellers to speak instead of type',
  },
  body: {
    id: 'SSqjfmSYSey-H4wlugRr',
    defaultMessage: 'Just talk through your trip — who’s going, dates, what you love.',
    description: 'Body of the tip under the TripPlanner landing page microphone button suggesting what to say when planning by voice',
  },
  accept: {
    id: '8O0cgtQ1CnWzlrGh8OA5',
    defaultMessage: 'Try it now',
    description: 'Button in the TripPlanner landing page voice tip that immediately starts recording a voice message',
  },
  dismiss: {
    id: 'CnPsGIPo3GW61KfI0P6z',
    defaultMessage: 'Got it',
    description: 'Button in the TripPlanner landing page voice tip that closes the tip and snoozes voice prompts without recording',
  },
})

const slideIn = keyframes`
  from { opacity: 0; translate: 0 ${rem(6)}; }
  to { opacity: 1; translate: none; }
`

const Card = styled(Sheet)`
  width: ${rem(290)};
  max-width: calc(100vw - ${rem(32)});
  z-index: ${zIndex.tooltip};
  animation: ${slideIn} 0.35s ease-out;

  &.closing {
    animation: ${fadeOut} 0.2s ease-in forwards;
  }
`

const Tail = styled.span`
  position: absolute;
  inset-block-end: 100%;
  width: ${rem(TAIL_SIZE * 2)};
  height: ${rem(TAIL_SIZE)};

  &.above {
    inset-block: 100% auto;
    rotate: 180deg;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    border-inline: ${rem(TAIL_SIZE)} solid transparent;
    border-block-end: ${rem(TAIL_SIZE)} solid;
  }

  &::before {
    inset-block-end: 0;
    border-block-end-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  /* Overlaps the card's border so the tail reads as part of the card. */
  &::after {
    inset-block-end: ${rem(-1)};
    border-block-end-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

interface Props {
  anchorElement: HTMLElement | null
  textDirection: TextDirection
  isClosing: boolean
  onAccept: () => void
  onDismiss: () => void
  onClosed: () => void
}

function VoiceLandingTip({ anchorElement, textDirection, isClosing, onAccept, onDismiss, onClosed }: Props) {
  const intl = useIntl()
  const { setFloating, floatingStyle, arrowRef, arrowStyle, isAbove } = useVoicePromptPosition(anchorElement, textDirection)

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') onDismiss()
  }, [onDismiss])

  const handleAnimationEnd = useCallback((event: AnimationEvent<HTMLDivElement>) => {
    if (isClosing && event.target === event.currentTarget) onClosed()
  }, [isClosing, onClosed])

  return (
    <Card
      ref={setFloating}
      style={floatingStyle}
      className={clsx({ closing: isClosing })}
      background="normal-background"
      border="light-foreground"
      borderRadius="M"
      shadow="bottom-medium"
      padding={16}
      onKeyDown={handleKeyDown}
      onAnimationEnd={handleAnimationEnd}
    >
      <Tail ref={arrowRef} style={arrowStyle} className={clsx({ above: isAbove })} />
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <BodyText variant="large" weight="bold">
            {intl.formatMessage(messages.title)}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {intl.formatMessage(messages.body)}
          </BodyText>
        </Group>
        <Group direction="horizontal" gap={8}>
          <TextButton kind="primary" size="small" shape="round" onClick={onAccept}>
            {intl.formatMessage(messages.accept)}
          </TextButton>
          <TextButton kind="secondary" size="small" shape="round" onClick={onDismiss}>
            {intl.formatMessage(messages.dismiss)}
          </TextButton>
        </Group>
      </Group>
    </Card>
  )
}

export default VoiceLandingTip
